import moment from 'moment-timezone';
import {
  equals,
  gt,
  join,
  length,
  lte,
  pathOr,
  replace,
  slice,
  split,
} from 'ramda';
import { ISO_FORMAT } from 'src/constants';
import { reportException } from 'src/exceptionReporting';
import store from 'src/store';
import { hasCorrectIntFormat } from './dashboard';
import { isNotEmpty, returnIsNotEmptyOrTrue } from './hasValue';
import { DateTime } from 'luxon';
import { getTimeZone } from './timezones/utilities';

export type TimeInterval = 'day' | 'week' | 'month' | 'year' | 'never';

const durationMap = {
  day: () => moment.duration(1, 'days'),
  week: () => moment.duration(1, 'weeks'),
  month: () => moment.duration(1, 'months'),
  year: () => moment.duration(1, 'years'),
  never: () => moment.duration(1000, 'years'),
};

const shouldHumanize = (
  time: moment.Moment,
  cutoff?: TimeInterval
): boolean => {
  // If cutoff is not provided, use the default ISO output.
  if (!cutoff) {
    return false;
  }
  const duration = durationMap[cutoff]();
  const diff = moment.duration(moment().diff(time));
  // Humanize the date if it is earlier than the cutoff:
  return diff <= duration;
};

export const getDurations = (date: any) => {
  return moment(date).fromNow();
};
interface FormatDateOptions {
  humanizeCutoff?: TimeInterval;
  format?: string;
  timezone?: string;
}

export const currentFormatDate = () => {
  return moment().format('MM/DD/YYYY');
};




/*
 *
 * RFC2822 timeformat
 * This format(Fri, 28 Nov 2014 21:00:09 +0900) is needed by the rust lang library chrono to
 * parse and convert to date.
 *
 * humanizeUTCDate takes a date string with rfc2822 format,
 * converts it to UTC and formats. The default format is MM/DD/YYYY
 *
 * @params {date} string date
 *         {options} object where a different format can be provided.
 */
export const humanizeUTCDate = (date: string) => {
  const mutc = moment.utc(date);
  return mutc.format('MMM DD YYYY');
};

export const getTommorowDate = () => {
  const tomorrow = moment(new Date()).add(1, 'day');
  const month = tomorrow.format('MM');
  const day = tomorrow.format('DD');
  const year = tomorrow.format('YYYY');
  return `${month}/${day}/${year}`;
};


export const getDayAfterTommorowDateWithFormat = () => {
  /**
   * Example: Applications were created at 10PM ET on Jan 19. The created_at was Jan 20 UTC
   * When we pulled all the loans from India, we were able to see them, as we pull up to Jan 21
   * When we pulled all the loans from US (ET), the werent listed that were created Jan 20.
   * Hence to support ET we use 2.
   * */
  const tomorrow = moment(new Date()).add(2, 'day');
  const month = tomorrow.format('MM');
  const day = tomorrow.format('DD');
  const year = tomorrow.format('YYYY');

  return `${year}/${month}/${day}`;
};



/*
 * RFC2822 timeformat
 * -------------------------
 * This format(Fri, 28 Nov 2014 21:00:09 +0900) is needed by the rust lang library chrono to
 * parse and convert to date.
 *
 * humanizeUTCDateAs takes a date string with format option,
 * converts it to UTC and formats. The default format is MM/DD/YYYY
 *
 * @params {date} string date
 *         {options} object where a different format can be provided. The
 *         default option is MM/DD/YYYY
 */
export const humanizeUTCDateAs = (
  date: string,
  opt: FormatDateOptions,
  defaultValue?: string
) => {
  const o = pathOr('MM/DD/YYYY', ['format'], opt);
  const mutc = moment.utc(date);
  if (!isNotEmpty(date)) {
    return defaultValue || '';
  }
  return mutc.format(o);
};

export const applicationDate = (date: string) => {
  const TIMEZONE = getTimeZone()
  return moment.utc(date).tz(TIMEZONE).format('DD MMM YYYY HH:mm:ss');
};
export const dateWithTimeStamp = (date: any,timeZone?:string) => {
  const TIMEZONE = isNotEmpty(timeZone) ? timeZone : getTimeZone()
  return moment.utc(date).tz(TIMEZONE).format('ddd, MMM DD, hh:mm a');
};
export const dateWithTimeStampForStages = (date: any) => {
  const TIMEZONE = getTimeZone()
  if (date) {
    return moment.utc(date).tz(TIMEZONE).format('ddd, MMM DD, hh:mm a');
  } else {
    return null;
  }
};

export const humanizeUTCDateWithoutTimeZone = (
  date: string,
  opt: FormatDateOptions
) => {
  const format = pathOr('MM/DD/YYYY', ['format'], opt);
  return moment(date).format(format);
};

export const humanizeUTCDateWithTimeZone = (
  date: string,
  opt: FormatDateOptions
) => {
  const TIMEZONE = getTimeZone()
  const format = pathOr('MM/DD/YYYY', ['format'], opt);
  return isNotEmpty(date) ? '' : moment.utc(date).tz(TIMEZONE).format(format);
};

/**
 * timeStampInISOFormat - will be returning local timestamp in ISO Format "2020-05-16T08:28:29.389+0000"
 */
export const timeStampInISOFormat = () => {
  return moment().utc().format('YYYY-MM-DDTHH:mm:ss.SSSZ');
};

export const nowTimestampTZ = (opt?: any) => {
  const TIMEZONE = getTimeZone()
  return moment().tz(TIMEZONE).format(
    pathOr('YYYY-MM-DDTHH:mm:ss.SSSZ', ['format'], opt)

  );
};
/**
 * convertTimeStampInISOFormat - will be returning given timestamp in ISO Format "2020-05-16T08:28:29.389+0000"
 */
export const convertTimeStampInISOFormat = (date: any) => {
  return moment(date).utc().format();
};

/**
 * convertTimeStampInISOFormat - will be returning without timestamp "2020-05-16"
 */
export const convertISOFormatWithoutTimeStamp = (date: any) => {
  return moment(date).utc().format('YYYY-MM-DD');
};

/**
 * convertISOFormatWithoutTimeStampDOB - will be returning without timestamp "2020-05-16"
 */
export const convertISOFormatWithoutTimeStampDOB = (date: any) => {
  return moment(date).format('YYYY-MM-DD');
};
/**
 *  it will return date in this format "Fri, May 16, 2020 1:31 am" in given timezone
 */
export const dateWithTimeStampAndYear = (timeStamp: string) => {
  const TIMEZONE = getTimeZone()
  if (timeStamp) {
    return moment(timeStamp).tz(TIMEZONE).format('ddd, MMM DD, YYYY, h:mm a');
  }
  return '';
};


/**
*  it will return date in this format "May 16, 2020" in given timezone
*/
export const dateWithTimeStampToHumanizeDate = (timeStamp: string) => {
  const TIMEZONE = getTimeZone()
  if (timeStamp) {
    return moment(timeStamp).tz(TIMEZONE).format('MMM DD, YYYY');
  }
  return '';
};

export const dateToHumanizeDateString = (date: Date, dateStyle: string = 'short') => {
  const TIMEZONE = getTimeZone()
  if (date) {
    return date.toLocaleString('en-US', {
      timeZone: TIMEZONE,
      dateStyle: dateStyle as any
    })
  }
  return '';
}

/**
 * getDaysDifferenceBetweenTwoDate - can return days that is difference between two dates
 * @param date can have date as ISO Format "2020-05-16T08:28:29.389+0000"
 * @param comparingDate can have date as ISO Format "2020-05-16T08:28:29.389+0000"
 */

export const getDaysDifferenceBetweenTwoDate = (
  date: any,
  comparingDate: any
) => {
  return moment(date).diff(moment(comparingDate), 'days');
};

/**
 * getMonthsDifferenceBetweenTwoDate - can return Months that is difference between two dates
 * @param date can have date as ISO Format "2020-05-16T08:28:29.389+0000"
 * @param comparingDate can have date as ISO Format "2020-05-16T08:28:29.389+0000"
 */

export const getMonthsDifferenceBetweenTwoDate = (
  date: any,
  comparingDate: any
) => {
  return moment(date).diff(moment(comparingDate), 'months');
};

/**
 * substractDaysFromGivenDate - return substracted date from given date and substractable days
 * @param date can have date as ISO Format "2020-05-16T08:28:29.389+0000"
 * @param days can have days as number as eg "21"
 */

export const substractDaysFromGivenDate = (date: string, days: number) => {
  return moment(date).subtract(days, 'days').format();
};

/**
 * addDaysFromGivenDate - return substracted date from given date and substractable days
 * @param date can have date as ISO Format "2020-05-16T08:28:29.389+0000"
 * @param days can have days as number as eg "21"
 */

export const addDaysFromGivenDate = (date: string, days: number, format?: string) => {
  return moment(date).add(days, 'days').format(returnIsNotEmptyOrTrue(format, format, ''));
};
/**
 * getStartDateOfLastMonthFromCurrentDateOrGivenDate - returns start date of previous month of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */
export const getStartDateOfLastMonthFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date).subtract(1, 'months').startOf('month').format();
  }
  return moment().subtract(1, 'months').startOf('month').format();
};



/**
 * getLastDateOfLastMonthFromCurrentDateOrGivenDate - returns last date of previous month of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */
export const getLastDateOfLastMonthFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date).subtract(1, 'months').endOf('month').format();
  }
  return moment().subtract(1, 'months').endOf('month').format();
};
/**
 * getStartDateOfLastQuaterFromCurrentDateOrGivenDate - returns start date of laster quarter of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */

export const getStartDateOfLastQuaterFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date)
      .quarter(moment().quarter() - 1)
      .startOf('quarter')
      .format();
  }
  return moment()
    .quarter(moment().quarter() - 1)
    .startOf('quarter')
    .format();
};

/**
 * getLastDateOfLastQuaterFromCurrentDateOrGivenDate - returns last date of last quarter of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */
export const getLastDateOfLastQuaterFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date)
      .quarter(moment().quarter() - 1)
      .endOf('quarter')
      .format();
  }
  return moment()
    .quarter(moment().quarter() - 1)
    .endOf('quarter')
    .format();
};

/**
 * getStartDateOfLastYearFromCurrentDateOrGivenDate - returns start date of last year of current date or for a given date
 * @param date  can have date ,if date is not given then current date will be used
 */
export const getStartDateOfLastYearFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date).subtract(1, 'years').startOf('year').format();
  }
  return moment().subtract(1, 'years').startOf('year').format();
};

/**
 * getLastDateOfLastYearFromCurrentDateOrGivenDate -  returns last date of last year of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */
export const getLastDateOfLastYearFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date).subtract(1, 'years').endOf('year').format();
  }
  return moment().subtract(1, 'years').endOf('year').format();
};

/**
 * getQuaterPeriodStartDateFromCurrentDateOrGivenDate - returns start date of current quarter of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */
export const getQuaterPeriodStartDateFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date)
      .quarter(moment(date).quarter())
      .startOf('quarter')
      .format();
  }
  return moment().quarter(moment().quarter()).startOf('quarter').format();
};

/**
 * getStartDateOfYearFromCurrentDateOrGivenDate - returns start date of current year of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */

export const getStartDateOfYearFromCurrentDateOrGivenDate = (date?: string) => {
  if (date) {
    return moment(date).startOf('year').format();
  }
  return moment().startOf('year').format();
};

/**
 * getStartDateOfMonthFromCurrentDateOrGivenDate - returns start date of current month of current date or for a given date
 * @param date can have date ,if date is not given then current date will be used
 */
export const getStartDateOfMonthFromCurrentDateOrGivenDate = (
  date?: string
) => {
  if (date) {
    return moment(date).startOf('month').format();
  }
  return moment().startOf('month').format();
};

export const formatDate = (
  date: string,
  options: FormatDateOptions = {}
): string | null => {
  let time;
  /** get the timezone from redux and use it as the moment timezone */
  const state = store.getState();
  const userTimezone = pathOr(
    'GMT',
    ['__resources', 'profile', 'data', 'timezone'],
    state
  );

  try {
    // Unknown error was causing this to crash in rare situations.
    time = moment.utc(Number(date)).tz(userTimezone);
  } catch (e) {
    // Better to return a blank date than an error or incorrect information.
    reportException(`<---formatDate-379--->${e}`);
    return 'Error getting date';
  }
  const formattedTime = shouldHumanize(time, options.humanizeCutoff)
    ? time.fromNow()
    : time.format(options.format || ISO_FORMAT);
  return formattedTime;
};

export const isGoodDate = (dt: string) => {
  // MM/DD/YYYY
  const reGoodDate =
    /^((0?[1-9]|1[012])[/.](0?[1-9]|[12][0-9]|3[01])[/.](19|20)?[0-9]{4})$/;
  return reGoodDate.test(dt);
};
export const dateFormatterFn = (date: any) => {
  const removeFirstSlash = replace('/', '', date);
  const removeAllSlash: any = replace('/', '', removeFirstSlash);
  if (equals(length(date), 3) && hasCorrectIntFormat(date)) {
    return join('', [slice(0, 2, date), '/', slice(2, 3, date)]);
  } else if (equals(length(date), 6) && hasCorrectIntFormat(removeFirstSlash)) {
    return join('', [slice(0, 5, date), '/', slice(5, 6, date)]);
  } else if (gt(length(date), 10)) {
    return undefined;
  } else if (
    lte(length(removeAllSlash), 10) &&
    hasCorrectIntFormat(removeAllSlash)
  ) {
    return date;
  }
};
export const dateFormatterFnWithDash = (date: any) => {
  const removeFirstSlash = replace('-', '', date);
  const removeAllSlash: any = replace('-', '', removeFirstSlash);
  if (equals(length(date), 5) && hasCorrectIntFormat(date)) {
    return join('', [slice(0, 4, date), '-', slice(4, 5, date)]);
  } else if (equals(length(date), 8) && hasCorrectIntFormat(removeFirstSlash)) {
    return join('', [slice(0, 7, date), '-', slice(7, 8, date)]);
  } else if (gt(length(date), 10)) {
    return undefined;
  } else if (
    lte(length(removeAllSlash), 10) &&
    hasCorrectIntFormat(removeAllSlash)
  ) {
    return date;
  }
};
export const dateFormatterWithDash = (date: any) => {
  return join('-', split('/', date));
};

export const getHumanizedCreatedAt = (data: any) => {
  const TIMEZONE = getTimeZone()
  return DateTime.fromISO(data?.created_at, { zone: 'utc' })
    .setZone(TIMEZONE)
    .toFormat('LLL dd, yyyy, hh:mm:ss a');
};



export const dobDateFormatter = (date: string, format: string = 'yyyy-MM-dd') => {
  const formattedDate = DateTime.fromFormat(date, 'dd/MM/yyyy');
  return formattedDate.toFormat(format);
};


/**
 * Formats a date based on the specified time zone.
 *
 * @param {string} date - The date to format.
 * @param {FormatDateOptions} [opt] - Optional formatting options.
 * @param {string} [defaultValue] - Default value to return if formatting fails.
 * @returns {string} - The formatted date string or the defaultValue if formatting fails.
 */

export const getHumanizedDateWithLuxon = (date: any, opt?: FormatDateOptions, defaultValue?: any) => {
  try {
    const format = pathOr('MM/dd/yyyy', ['format'], opt);
    const dateTime = DateTime.fromJSDate(new Date(date));
    const formattedDate = dateTime.toFormat(format);
    if (!dateTime.isValid) {
      return defaultValue || '';
    }
    return formattedDate;
  } catch (error) {
    reportException(`<---------formateDate.ts-482--------->${error}`);
    return defaultValue || '';
  }
};

export default formatDate;

export const getNumberOfYearsAndMonths = (startDate: string, endDate?: string) => {
  const utcDate = endDate ? moment(endDate) : moment.utc()
  const duration = moment.duration(utcDate.diff(startDate));
  const years = duration.years();
  const months = duration.months();
  return {
    years,
    months,
  };
};

/**
 * Calculate the number of days that have passed since the given date in its timezone.
 *
 * @param {string} givenDateISO - The ISO format of the given date and time.
 * @returns {number} The number of days that have passed since the given date.
 */
export const calculateDaysPassedSinceGivenDate = (date: string) => {
  const TIMEZONE = getTimeZone()
  const givenDate = DateTime.fromISO(date, { zone: 'utc' })
    .setZone(TIMEZONE)
  const currentDate = DateTime.now().setZone(TIMEZONE);
  const daysPassed = currentDate.diff(givenDate, 'days').days;
  return daysPassed

}
